import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Bot, Table, BarChart3, RefreshCw, ChevronDown, ChevronUp, Database } from 'lucide-react';
import UserMessage from './MessageBubble/UserMessage';
import DataVisualization from './MessageBubble/DataVisualization';
import SQLDisplay from './MessageBubble/SQLDisplay';
import DataTable from './MessageBubble/DataTable';
import ConfidenceScoring from './MessageBubble/ConfidenceScoring';
import ContextualSuggestions from './MessageBubble/ContextualSuggestions';
import GoalBrief from './GoalBrief';
import FeedbackButton from './FeedbackButton';
import FeedbackModal from './FeedbackModal';

// Language detection helper
const detectLanguage = (text) => {
  if (!text) return 'english';
  
  // Check for Arabic characters
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
  
  if (arabicRegex.test(text)) {
    return 'arabic';
  }
  
  return 'english';
};

const MessageContainer = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  width: 100%;
  direction: ltr;  /* Force LTR for consistent layout - bot messages on left */

  ${props => props.isUser && `
    justify-content: flex-end;
  `}
`;

const UserMessageStyled = styled.div`
  background: linear-gradient(135deg, #1B8C3C 0%, #006200 100%);
  color: white;
  padding: 16px 20px;
  border-radius: 20px 20px 4px 20px;
  max-width: 80%;
  box-shadow: 0 4px 12px rgba(27,140,60, 0.3);
  margin-left: auto;

  .content {
    font-weight: 500;
    line-height: 1.5;
    direction: auto;
    text-align: start;
    unicode-bidi: plaintext;
  }

  .timestamp {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.8);
    margin-top: 4px;
  }
`;

const BotMessageStyled = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 100%;  /* Allow full width for charts */
  width: 100%;
  direction: ltr;  /* Force LTR for layout - bot avatar on left */
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.isUser 
    ? 'linear-gradient(135deg, #1B8C3C 0%, #006200 100%)'
    : 'linear-gradient(135deg, #4FB8B8 0%, #1B8C3C 100%)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
    color: white;
  }
`;

const BubbleContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 20px 20px 20px 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  width: ${props => props.$hasChart ? 'fit-content' : '100%'};
  min-width: ${props => props.$hasChart ? '950px' : '700px'};  /* Wider for chart+legend+padding */
  max-width: 100vw;  /* Allow full viewport width */
  overflow-x: auto;

  .main-content {
    color: #2d3748;
    line-height: 1.6;
    font-size: 15px;
    direction: auto;
    text-align: start;
    unicode-bidi: plaintext;
    margin-bottom: 12px;
    white-space: pre-wrap;   /* preserve the answer's line breaks (bulleted breakdown) */
  }
  
  .main-content.arabic {
    direction: rtl;
    text-align: right;
  }
  
  .main-content.english {
    direction: ltr;
    text-align: left;
  }

  .timestamp {
    font-size: 12px;
    color: #718096;
    margin-bottom: 16px;
  }
`;

const SQLSection = styled.div`
  margin-top: 16px;

  .label {
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .sql-container {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    max-width: 100%;
    
    pre {
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      max-width: 100% !important;
      overflow-x: visible !important;
    }
    
    code {
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
    }
  }

  .content-text {
    background: #f8f9fa;
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid #e9ecef;
    color: #495057;
    line-height: 1.5;
    white-space: pre-wrap;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  }

  .steps-container {
    background: #f8f9fa;
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }

  .step-item {
    margin-bottom: 8px;
    color: #495057;
    line-height: 1.5;
    
    &:last-child {
      margin-bottom: 0;
    }

    strong {
      color: #212529;
      font-weight: 600;
    }
  }
`;

const DataTableSection = styled.div`
  margin-top: 16px;

  .label {
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const TableContainer = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const TableHeader = styled.thead`
  background: linear-gradient(135deg, #1B8C3C 0%, #006200 100%);
  
  th {
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    color: white;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    font-size: 13px;
    text-transform: capitalize;
    
    &:not(:last-child) {
      border-right: 1px solid rgba(255, 255, 255, 0.1);
    }
  }
`;

const TableBody = styled.tbody`
  tr {
    transition: background-color 0.2s ease;
    
    &:nth-child(even) {
      background-color: #f8fafc;
    }
    
    &:hover {
      background-color: #edf2f7;
    }
    
    &:last-child td {
      border-bottom: none;
    }
  }
  
  td {
    padding: 12px 16px;
    border-bottom: 1px solid #e2e8f0;
    color: #2d3748;
    vertical-align: top;
    line-height: 1.4;
    
    &:not(:last-child) {
      border-right: 1px solid #f1f5f9;
    }
  }
`;

const TableMeta = styled.div`
  padding: 8px 16px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  color: #718096;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NoDataMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #718096;
  font-style: italic;
  background: #f8fafc;
`;

const TableExpandControl = styled.div`
  padding: 12px 16px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: center;
  
  button {
    background: linear-gradient(135deg, #1B8C3C 0%, #006200 100%);
    color: white;
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(27,140,60, 0.3);
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const VisualizationSection = styled.div`
  margin-top: 16px;

  .label {
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    direction: rtl;
    text-align: right;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const VegaContainer = styled.div`
  background: #f8fafc;
  border: 2px dashed #4299e1;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  margin-top: 8px;
  width: 100%;
  position: relative;

  .chart-container {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-top: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    width: 100%;
    min-height: 400px;
    overflow: auto;
    
    /* Ensure proper container for Vega-Lite */
    & > div {
      width: 100% !important;
      min-height: 350px;
    }
    
    /* Natural chart sizing but with constraints */
    svg {
      max-width: 100% !important;
      height: auto !important;
    }
  }
`;

const ChartActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;

const AdjustButton = styled(motion.button)`
  background: linear-gradient(135deg, #1B8C3C 0%, #006200 100%);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(27,140,60, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const DataSection = styled.div`
  background: ${props => {
    switch (props.type) {
      case 'formatted_result': return '#f7fafc';
      case 'raw_value': return '#f0fff4';
      case 'suggestion': return '#fffbeb';
      default: return '#f7fafc';
    }
  }};
  border: 1px solid ${props => {
    switch (props.type) {
      case 'formatted_result': return '#e2e8f0';
      case 'raw_value': return '#c6f6d5';
      case 'suggestion': return '#fed7aa';
      default: return '#e2e8f0';
    }
  }};
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;

  .label {
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    direction: rtl;
    text-align: right;
  }

  .content {
    color: ${props => {
      switch (props.type) {
        case 'raw_value': return '#22543d';
        case 'suggestion': return '#92400e';
        default: return '#2d3748';
      }
    }};
    font-family: ${props => props.type === 'formatted_result' ? 'monospace' : 'inherit'};
    font-size: ${props => props.type === 'raw_value' ? '18px' : '14px'};
    font-weight: ${props => props.type === 'raw_value' ? '600' : 'normal'};
    text-align: ${props => props.type === 'raw_value' ? 'center' : 'left'};
    line-height: ${props => props.type === 'reasoning' ? '1.6' : 'inherit'};
    white-space: ${props => props.type === 'adjustment_feedback' ? 'pre-wrap' : 'normal'};
  }
`;

const NoVisualization = styled.div`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  color: #718096;
  font-style: italic;
`;

const ConfidenceScoringSection = styled.div`
  margin-top: 16px;

  .label {
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const ConfidenceContainer = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-top: 8px;
`;

const ConfidenceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const ConfidenceMetric = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .metric-icon {
    width: 24px;
    height: 24px;
    margin-bottom: 8px;
    color: ${props => {
      switch (props.type) {
        case 'quality': return '#4299e1';
        case 'consistency': return '#38a169';
        case 'definitions': return '#ed8936';
        case 'hardness': return '#9f7aea';
        default: return '#718096';
      }
    }};
  }

  .metric-label {
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .metric-value {
    font-size: ${props => props.isNumeric ? '20px' : '14px'};
    font-weight: ${props => props.isNumeric ? '700' : '600'};
    color: ${props => {
      if (!props.isNumeric) return '#718096';
      const value = parseFloat(props.value);
      if (value >= 80) return '#38a169';
      if (value >= 60) return '#ed8936';
      return '#e53e3e';
    }};
    margin-bottom: 4px;
  }

  .metric-description {
    font-size: 11px;
    color: #718096;
    line-height: 1.3;
  }
`;

const ConfidenceUnavailable = styled.div`
  background: #fed7d7;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  color: #742a2a;
  font-size: 14px;
  font-style: italic;
`;

const ReformulationInfo = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 13px;
  direction: rtl;

  .reformulation-header {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #0369a1;
    font-weight: 600;
    margin-bottom: 10px;
    font-size: 12px;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .question-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 6px;
  }

  .question-label {
    color: #64748b;
    font-size: 11px;
    min-width: 80px;
    flex-shrink: 0;
  }

  .question-text {
    color: #334155;
    flex: 1;
  }

  .original-question {
    text-decoration: line-through;
    opacity: 0.7;
  }

  .reformulated-question {
    color: #0369a1;
    font-weight: 500;
  }

  .arrow {
    color: #0ea5e9;
    margin: 4px 0;
    text-align: center;
  }
`;


const MessageActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
`;

const DetailsSection = styled.div`
  margin-top: 12px;
  border-top: 1px dashed #e2e8f0;
  padding-top: 8px;
  .details-toggle {
    display: flex; align-items: center; gap: 7px;
    background: none; border: none; cursor: pointer;
    color: #64748b; font-size: 12.5px; font-weight: 700; padding: 6px 0;
  }
  .details-toggle:hover { color: #006200; }
  .details-body { margin-top: 6px; }
`;

function MessageBubble({ message, onSendMessage, onApproveGoal, sessionId, userQuestion }) {
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  // Answer-first: the SQL + raw table + confidence are folded by default (verification, on demand),
  // so the default view is the answer + chart. Always available, never forced.
  const [showDetails, setShowDetails] = useState(false);

  // Check if chart can be adjusted (allow adjustment even if current chart failed)
  const hasValidData = message.column_names &&
                       message.results &&
                       message.results.length > 0;

  const canAdjustChart = hasValidData;

  const handleOpenFeedbackModal = () => {
    setShowFeedbackModal(true);
  };

  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
  };

  if (message.isUser) {
    return <UserMessage message={message} />;
  }

  // Goal Agent (Mode 5): a live decision-brief message in the chat history.
  if (message.messageType === 'goal') {
    return (
      <MessageContainer initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }}>
        <GoalBrief
          goalStatus={message.goalStatus}
          goal={message.goal}
          plan={message.plan}
          planRationales={message.planRationales}
          planReasoning={message.planReasoning}
          planNote={message.planNote}
          verdicts={message.verdicts}
          answerabilityNote={message.answerabilityNote}
          subAnswers={message.subAnswers}
          brief={message.brief}
          error={message.error}
          onExplore={onSendMessage}
          onExploreQuestion={onSendMessage}
          onSynthesize={(approvedQuestions) => onApproveGoal && onApproveGoal(message.id, message.goal, approvedQuestions, message.answerabilityNote, message.goalType, message.planReasoning)}
        />
      </MessageContainer>
    );
  }

  return (
    <MessageContainer
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <BotMessageStyled>
        <Avatar>
          <Bot />
        </Avatar>
        <BubbleContent $hasChart={!!(message.visualization?.vega_lite_spec)}>
          {/* Show reformulation info if question was reformulated */}
          {message.reformulation && (
            <ReformulationInfo>
              <div className="reformulation-header">
                <RefreshCw />
                <span>تم إعادة صياغة السؤال للوضوح</span>
              </div>
              <div className="question-row">
                <span className="question-label">السؤال الأصلي:</span>
                <span className="question-text original-question">
                  {message.reformulation.original_question}
                </span>
              </div>
              <div className="arrow">↓</div>
              <div className="question-row">
                <span className="question-label">السؤال المُرسَل:</span>
                <span className="question-text reformulated-question">
                  {message.reformulation.reformulated_question}
                </span>
              </div>
            </ReformulationInfo>
          )}
          {/* ANSWER-FIRST: the real answer summary up top (extracted from final_answer, before its
              SQL-explanation), so the decision-relevant text leads — not buried under the SQL. */}
          {(() => {
            const answerText = message.final_answer
              ? message.final_answer.split('شرح إستعلام SQL')[0].trim()
              : message.content;
            return answerText
              ? <div className={`main-content ${detectLanguage(answerText)}`}>{answerText}</div>
              : null;
          })()}
          <div className="timestamp">{message.timestamp}</div>

          {/* ── ANSWER-FIRST: the chart comes right after the answer (digestible, decision-relevant) ── */}
          {(message.visualization && Object.keys(message.visualization).length > 0) && (
            <VisualizationSection>
              <div className="label">
                <BarChart3 />
                تصور البيانات:
              </div>
              <DataVisualization message={message} userQuestion={userQuestion} />
            </VisualizationSection>
          )}

          {/* No saved chart but we have data → render an adjustable chart in its place */}
          {!message.visualization && canAdjustChart && (
            <VisualizationSection>
              <div className="label">
                <BarChart3 />
                تصور البيانات:
              </div>
              <DataVisualization
                message={{
                  visualization: { raw_data: message.column_names && message.results ?
                    message.results.map(row => {
                      const obj = {};
                      message.column_names.forEach((col, index) => {
                        obj[col] = row[index];
                      });
                      return obj;
                    }) : null },
                  sql_query: message.sql_query,
                  column_names: message.column_names,
                  results: message.results
                }}
                userQuestion={userQuestion}
              />
            </VisualizationSection>
          )}

          {/* Results table — kept VISIBLE right under the chart (the numbers are the substance;
                BI users want them). The table itself caps rows with its own expand control. */}
          {(message.column_names || message.results) && (
            <DataSection>
              <div className="label">
                <Table />
                نتائج الاستعلام:
              </div>
              <DataTable
                columnNames={message.column_names}
                results={message.results}
              />
            </DataSection>
          )}

          {/* What's next — clearly separated: drill into THIS result first, then related questions. */}
          {onSendMessage && message.drillSuggestions && message.drillSuggestions.length > 0 && (
            <ContextualSuggestions
              suggestions={message.drillSuggestions}
              label="🔎 تعمّق في هذه النتيجة:"
              onSuggestionClick={(query) => onSendMessage(query)}
            />
          )}
          {onSendMessage && message.suggestions && (
            <ContextualSuggestions
              suggestions={message.suggestions}
              label="➡️ أسئلة ذات صلة:"
              onSuggestionClick={(query) => onSendMessage(query)}
            />
          )}

          {/* ── Explainability — folded by default. The "how": the SQL, its plain-language
                 explanation, and confidence. Available for the analyst/auditor; the business
                 reader gets the answer + chart + data above without this noise. ── */}
          {(message.sql_query || message.final_answer || message.confidence_scoring) && (
            <DetailsSection>
              <button type="button" className="details-toggle" onClick={() => setShowDetails(v => !v)}>
                {showDetails ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                <Database size={14} />
                التوضيح: كيف توصّلنا لهذه الإجابة
              </button>
              {showDetails && (
                <div className="details-body">
                  <SQLDisplay message={message} showAnswer={false} />
                  <ConfidenceScoring confidenceScoring={message.confidence_scoring} />
                </div>
              )}
            </DetailsSection>
          )}

          {/* Feedback Button - only show for bot messages with an ID */}
          {message.id && (
            <MessageActions>
              <FeedbackButton
                messageId={message.id}
                sessionId={sessionId}
                onOpenModal={handleOpenFeedbackModal}
              />
            </MessageActions>
          )}
        </BubbleContent>
      </BotMessageStyled>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={handleCloseFeedbackModal}
        messageId={message.id}
        sessionId={sessionId}
      />
    </MessageContainer>
  );
}

export default MessageBubble; 